import * as React from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from './utils';
import { Slot } from 'radix-ui';

const buttonVariants = cva(
  // transition-colors, not transition-all: `all` puts box-shadow on the
  // transition list, so the focus ring fades in over the transition
  // duration instead of painting on the frame focus lands -- long enough for
  // a keyboard user mid-tab, or any synchronous check, to see zero spread.
  "inline-flex shrink-0 items-center justify-center gap-2 rounded-md text-sm font-medium whitespace-nowrap transition-colors outline-none focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50 disabled:pointer-events-none disabled:opacity-50 aria-invalid:border-destructive aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4",
  {
    variants: {
      variant: {
        default:
          'border border-primary-ink bg-primary text-primary-foreground hover:bg-primary/90 active:bg-primary-hover',
        // Full opacity in both themes, and the focus ring stays --ring: the
        // token pair is certified on the surface it is painted on, and a ring at
        // 20% or 40% of any token cannot reach 1.4.11's 3:1.
        destructive:
          'bg-destructive text-destructive-foreground hover:bg-destructive/90 active:bg-destructive-hover',
        outline:
          'border bg-background shadow-xs hover:bg-accent hover:text-accent-foreground active:bg-border dark:border-input dark:bg-input/30 dark:hover:bg-input/50 dark:active:bg-input/70',
        secondary: 'bg-secondary text-secondary-foreground hover:bg-secondary/80 active:bg-border',
        ghost:
          'hover:bg-accent hover:text-accent-foreground active:bg-border dark:hover:bg-accent/50 dark:active:bg-accent',
        link: 'text-primary-ink underline-offset-4 hover:underline active:text-primary-quiet-foreground',
      },
      size: {
        default: 'h-9 px-4 py-2 has-[>svg]:px-3',
        xs: "h-6 gap-1 rounded-md px-2 text-xs has-[>svg]:px-1.5 [&_svg:not([class*='size-'])]:size-3",
        // A `link` that sits in a line of text: no box of its own, so it aligns with the text around it.
        inline: "h-auto gap-1 p-0 text-xs [&_svg:not([class*='size-'])]:size-3",
        sm: 'h-8 gap-1.5 rounded-md px-3 has-[>svg]:px-2.5',
        lg: 'h-10 rounded-md px-6 has-[>svg]:px-4',
        icon: 'size-9',
        'icon-xs': "size-6 rounded-md [&_svg:not([class*='size-'])]:size-3",
        'icon-sm': 'size-8',
        'icon-lg': 'size-10',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  },
);

function Button({
  className,
  variant = 'default',
  size = 'default',
  asChild = false,
  ...props
}: React.ComponentProps<'button'> &
  VariantProps<typeof buttonVariants> & {
    asChild?: boolean;
  }) {
  const Comp = asChild ? Slot.Root : 'button';

  return (
    <Comp
      data-slot="button"
      data-variant={variant}
      data-size={size}
      className={cn(buttonVariants({ variant, size, className }))}
      {...props}
    />
  );
}

export { Button, buttonVariants };
